<template>
    <div class="spo-card rounded bordered pa-3">
        <div class="text-h6 font-weight-bold mb-3">Créneaux existants</div>
        <div v-if="loading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <template v-else>
            <v-row v-if="list_creneaux.length > 0" class="mb-3">
                <v-col cols="12" md="6">
                    <label class="field_label">Course</label>
                    <v-select v-model="filter_id_course" :items="coursesFilterOptions" item-title="text"
                        item-value="value" density="compact" hide-details clearable></v-select>
                </v-col>
                <v-col cols="12" md="6">
                    <label class="field_label">Nom du créneau</label>
                    <v-select v-model="filter_nom" :items="creneauNomFilterOptions" item-title="text" item-value="value"
                        density="compact" hide-details clearable></v-select>
                </v-col>
            </v-row>
            <v-table v-if="filtered_creneaux.length > 0">
                <thead>
                    <tr>
                        <th class="text-left">Nom</th>
                        <th class="text-left">Course</th>
                        <th class="text-left">Inscrits / Capacité</th>
                        <th class="text-left">Ouvert</th>

                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in filtered_creneaux" :key="item.id">
                        <td class="d-flex align-center ga-2 flex-wrap">
                            <span>{{ item.nom }}</span>
                            <v-chip v-if="isCreneauComplet(item)" size="small" color="error" variant="tonal">
                                Complet
                            </v-chip>
                        </td>
                        <td>{{ courseLabel(item.id_course) }}</td>
                        <td class="d-flex align-center ga-1">
                            <span>{{ item.nb_inscrits || 0 }} /</span>
                            <v-text-field v-model.number="item.capacity" type="number" min="0" density="compact"
                                hide-details class="creneau-capacity-field"></v-text-field>
                            <SportipsButton secondary-action-button icon="mdi-content-save"
                                :loading="savingCapacityId === item.id"
                                :disabled="savingCapacityId === item.id || togglingId === item.id"
                                @onClick="saveCapacity(item)"></SportipsButton>
                        </td>
                        <td>
                            <v-switch :model-value="isOpen(item)" hide-details density="compact" color="primary"
                                :disabled="togglingId === item.id"
                                @update:model-value="onToggleOpen(item, $event)"></v-switch>
                        </td>

                    </tr>
                </tbody>
            </v-table>
            <v-alert v-else-if="list_creneaux.length > 0" variant="tonal" color="info">Aucun créneau ne correspond aux
                filtres.</v-alert>
            <v-alert v-else variant="tonal" color="info">Aucun créneau pour ce module</v-alert>
        </template>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import PublicService from "@/services/public.service";
import AdminService from "@/services/admin.service";

export default {
    data() {
        return {
            list_creneaux: [],
            courses: [],
            loading: false,
            savingCapacityId: null,
            togglingId: null,
            filter_id_course: null,
            filter_nom: null,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        coursesFilterOptions() {
            const rest = this.courses.map((c) => ({ text: c.nom, value: c.id }));
            return [{ text: "Toutes les courses", value: null }, ...rest];
        },
        creneauNomFilterOptions() {
            const seen = new Set();
            const rows = [];
            for (const item of this.list_creneaux) {
                const n = String(item.nom ?? "").trim();
                if (seen.has(n)) continue;
                seen.add(n);
                rows.push({ text: n || "(Sans nom)", value: n });
            }

            return [{ text: "Tous les créneaux", value: null }, ...rows];
        },
        filtered_creneaux() {
            const cid = this.filter_id_course;
            const hasCourse = cid !== null && cid !== undefined && cid !== "";
            const hasNomFilter = this.filter_nom !== null && this.filter_nom !== undefined;
            return this.list_creneaux.filter((item) => {
                if (hasCourse) {
                    const ids = String(item.id_course ?? "")
                        .split(";")
                        .map((s) => s.trim());
                    const fid = String(cid);
                    if (!ids.includes(fid) && String(item.id_course) !== fid) return false;
                }
                if (hasNomFilter) {
                    const itemNom = String(item.nom ?? "").trim();
                    if (itemNom !== this.filter_nom) return false;
                }
                return true;
            });
        },
    },
    methods: {
        courseLabel(id_course) {
            if (id_course == "1345;1419;1420") return "tOur du Lac 100k en Relais";
            const c = this.courses.find((x) => String(x.id) === String(id_course));
            return c ? c.nom : `Course #${id_course}`;
        },
        isOpen(item) {
            return item.is_open == 1 || item.is_open === true || item.is_open === "1";
        },
        isCreneauComplet(item) {
            const cap = Number(item.capacity);
            const nb = Number(item.nb_inscrits ?? 0);
            if (!Number.isFinite(cap) || cap <= 0) {
                return false;
            }
            return nb >= cap;
        },
        async loadCourses() {
            if (!this.mainStore.module?.id) return;
            const res = await AdminService.getCourses(this.mainStore.module.id);
            this.courses = Array.isArray(res) ? res : [];
        },
        async refresh() {
            if (!this.mainStore.module?.id) return;
            this.loading = true;
            await this.loadCourses();
            const response = await PublicService.getListCreneaux(this.mainStore.module.id);
            this.loading = false;
            if (response && Array.isArray(response)) {
                this.list_creneaux = response;
            } else {
                this.list_creneaux = [];
            }
            const noms = new Set(
                this.list_creneaux.map((item) => String(item.nom ?? "").trim())
            );
            if (this.filter_nom !== null && !noms.has(this.filter_nom)) {
                this.filter_nom = null;
            }
        },
        async saveCapacity(item) {
            const cap = Number(item.capacity);
            if (Number.isNaN(cap) || cap < 0) {
                this.mainStore.notification.send("Indiquez une capacité valide (nombre positif ou zéro).", "error");
                return;
            }
            this.savingCapacityId = item.id;
            const res = await AdminService.updateCreneauRetraitDossard({ id: item.id, capacity: cap });
            this.savingCapacityId = null;
            if (res === false || res?.error) {
                this.mainStore.notification.send(
                    typeof res?.error === "string" ? res.error : "Impossible d'enregistrer la capacité.",
                    "error"
                );
                return;
            }
            this.mainStore.notification.send("Capacité mise à jour.", "success");
            await this.refresh();
        },
        async onToggleOpen(item, value) {
            const newOpen = value ? 1 : 0;
            this.togglingId = item.id;
            const res = await AdminService.updateCreneauRetraitDossard({ id: item.id, is_open: newOpen });
            this.togglingId = null;
            if (res === false || res?.error) {
                this.mainStore.notification.send(
                    typeof res?.error === "string" ? res.error : "Impossible de modifier l'état du créneau.",
                    "error"
                );
                return;
            }
            item.is_open = newOpen;
            this.mainStore.notification.send(newOpen ? "Créneau rouvert." : "Créneau fermé.", "success");
        },
    },
    async mounted() {
        await this.refresh();
    },
};
</script>

<style lang="scss" scoped>
.creneau-capacity-field {
    max-width: 120px;
}
</style>
